import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ProgressBar, PledgeForm } from './HannoverPage';
import { assetUrl } from '../shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#c8922a';

const FACTS = [['Fläche', 'ca. 216 m²'], ['Miete + NK', '~2.162 €/Mon.'], ['Umbau', '10–15.000 €']];

// Auf welchen Pfaden das Popup NICHT erscheinen soll (Admin/Kiosk/Auth-Flows + Bibelstudium).
const HIDE_ON = ['/admin', '/checkin', '/login', '/register', '/reset-password', '/bibelstudium', '/track', '/sendung', '/dashboard', '/scripturia', '/willkommen'];

export default function HannoverPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadSummary = useCallback(() => {
    axios.get(`${API_URL}/api/hannover/summary`).then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    try { localStorage.setItem('hannoverPopupDismissed', '1'); } catch (e) {}
  }, []);

  useEffect(() => {
    if (HIDE_ON.some(p => location.pathname.startsWith(p))) return;
    // Nicht über dem Bibelstudium-Bereich zeigen (Header-Link #bibelstudium bzw. ?studium=1)
    if (location.hash === '#bibelstudium' || new URLSearchParams(location.search).has('studium')) return;
    if (localStorage.getItem('hannoverPopupDismissed')) return; // dauerhaft weggeklickt
    if (sessionStorage.getItem('hannoverPopupShown')) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/site-content/hannover_popup_enabled`)
      .then(r => {
        const v = r.data && r.data.value;
        if (cancelled) return;
        if (v === 'off') return; // Standard = an, nur bei explizitem "off" aus
        loadSummary();
        const timer = setTimeout(() => {
          setOpen(true);
          sessionStorage.setItem('hannoverPopupShown', '1');
        }, 5000);
        // Timer merken zum Aufräumen
        HannoverPopup._t = timer;
      })
      .catch(() => {});
    return () => { cancelled = true; if (HannoverPopup._t) clearTimeout(HannoverPopup._t); };
    // eslint-disable-next-line
  }, []);

  if (!open) return null;
  const s = summary || {};

  return (
    <div onClick={dismiss} data-testid="hannover-popup" style={{ position: 'fixed', inset: 0, zIndex: 13000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', background: '#141210', color: '#fff', borderRadius: 18, boxShadow: '0 24px 70px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', position: 'sticky', top: 0, background: '#141210', borderBottom: '1px solid rgba(255,255,255,0.08)', zIndex: 2 }}>
          <span style={{ letterSpacing: 2.5, color: GOLD, fontWeight: 800, fontSize: 12.5 }}>PROJEKT HANNOVER · IN PLANUNG</span>
          <button onClick={dismiss} data-testid="hannover-popup-close" style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 20 }}>
          {s.banner_image_id ? (
            <Link to="/hannover" onClick={dismiss} style={{ display: 'block', marginBottom: 16 }}>
              <img src={assetUrl(s.banner_image_id)} alt="Projekt Hannover" data-testid="hannover-popup-banner"
                style={{ width: '100%', borderRadius: 12, display: 'block' }} />
            </Link>
          ) : (
            <h2 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 900, lineHeight: 1.2 }}>{s.title || 'Ein Ort, an dem Gemeinde gelebt wird.'}</h2>
          )}

          <p style={{ color: '#cfc8bd', fontSize: 15, lineHeight: 1.65, margin: '0 0 16px' }}>
            Wir möchten in Hannover Räume für Begegnung, Zurüstung und Aussendung anmieten. Damit dieser Ort entstehen und dauerhaft bestehen kann,
            brauchen wir monatliche Unterstützung – jeder Beitrag zählt.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            {FACTS.map(([k, v]) => (
              <div key={k} style={{ flex: '1 1 130px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '8px 12px' }}>
                <div style={{ fontSize: 10.5, color: '#a79f92', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                <div style={{ fontWeight: 800, color: '#fff', fontSize: 14 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.96)', color: '#1a1a1a', borderRadius: 14, padding: 18, marginBottom: 18 }}>
            <div style={{ fontWeight: 800, marginBottom: 12, textAlign: 'center', fontSize: 14 }}>Spendenstand (anonym){s.supporter_count ? ` · ${s.supporter_count} Unterstützer` : ''}</div>
            <ProgressBar label="Monatlich" total={s.monthly_total} goal={s.monthly_goal} unit="monthly" />
            <ProgressBar label="Umbau (einmalig)" total={s.onetime_total} goal={s.onetime_goal} unit="onetime" />
          </div>

          {!showForm ? (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => setShowForm(true)} data-testid="hannover-popup-pledge-btn" className="btn-primary" style={{ flex: '1 1 160px', background: GOLD, border: 'none' }}>Jetzt mittragen</button>
              <Link to="/hannover" onClick={dismiss} data-testid="hannover-popup-more-btn" className="btn-secondary" style={{ flex: '1 1 160px', textAlign: 'center', textDecoration: 'none' }}>Mehr zum Projekt →</Link>
            </div>
          ) : (
            <div style={{ background: '#fff', color: '#1a1a1a', borderRadius: 12, padding: 16 }} data-testid="hannover-popup-form">
              <PledgeForm summary={summary} onDone={loadSummary} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
